// Authentication context
import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '@/lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requiresMFA, setRequiresMFA] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('access_token');
    if (token) {
      try {
        const response = await authAPI.getCurrentUser();
        setUser(response.data);
      } catch (error) {
        console.error('Auth check failed:', error);
        localStorage.removeItem('access_token');
      }
    }
    setLoading(false);
  };

  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials);
      const { access_token, requires_mfa } = response.data;

      if (requires_mfa) {
        localStorage.setItem('temp_token', access_token);
        setRequiresMFA(true);
        return { requiresMFA: true };
      }

      localStorage.setItem('access_token', access_token);
      await checkAuth();
      return { success: true };
    } catch (error) {
      throw error;
    }
  };

  const verifyMFA = async (token) => {
    try {
      const response = await authAPI.verifyMFA(token);
      const { access_token } = response.data;

      localStorage.removeItem('temp_token');
      localStorage.setItem('access_token', access_token);
      setRequiresMFA(false);
      await checkAuth();
      return { success: true };
    } catch (error) {
      throw error;
    }
  };

  const register = async (userData) => {
    try {
      await authAPI.register(userData);
      return { success: true };
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    const token = localStorage.getItem('access_token');
    try {
      if (token) {
        await authAPI.logout();
      }
    } catch (error) {
      console.error('Logout request failed:', error);
    } finally {
      localStorage.removeItem('access_token');
      setUser(null);
    }
  };

  const value = {
    user,
    loading,
    requiresMFA,
    login,
    verifyMFA,
    register,
    logout,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
